"use client";

import { Fragment, useState } from "react";

import { useOutsideClick } from "@/hooks/use-outside-click";
import { classNames } from "@/utils/styling-utils";
import { HideOrShowFiltersButton } from "../filter/hide-or-show-filters-button";
import { HorizontalContainer } from "../layout/horizontal-container";
import { HorizontalSpacer } from "../layout/horizontal-spacer";
import { VerticalSpacer } from "../layout/vertical-spacer";
import { TextSkeleton } from "../skeletons/text-skeleton";
import { Text } from "../ui/text";
import type { FormStep, SelectedFormValues } from "./types";

interface StepIndicatorAllProps {
  currentStepIndex: number;
  goTo?: (index: number) => void;
  selectedValues?: SelectedFormValues;
}

interface StepIndicatorProps extends StepIndicatorAllProps {
  hideFiltersButton?: boolean;
  numberOfSteps: number;
  steps: FormStep[];
}

export const StepIndicator = ({
  currentStepIndex,
  goTo,
  hideFiltersButton,
  numberOfSteps,
  selectedValues,
  steps,
}: StepIndicatorProps) => {
  const totalWidth = numberOfSteps - 1;

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const ref = useOutsideClick(() => {
    if (isOpen) {
      setIsOpen(false);
    }
  });

  return (
    <div className="step-indicator-wrapper">
      <div className="step-indicator" ref={ref}>
        <div className="step-indicator__steps-and-button">
          <div
            className={classNames(
              "step-indicator__steps-and-button__steps",
              hideFiltersButton && "step-indicator__steps-and-button__steps--no-button",
            )}
          >
            {Array.from({ length: numberOfSteps }, (_, i) => (
              <div
                className="step-indicator__steps-and-button__steps__step"
                // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                key={i}
                style={i < numberOfSteps - 1 ? { width: `calc((100%) / ${totalWidth})` } : {}}
              >
                <StepIndicatorCircle
                  currentStepIndex={currentStepIndex}
                  goTo={goTo}
                  selectedValues={selectedValues}
                  stepIndex={i}
                />

                <div className="step-indicator__steps-and-button__steps__step__line" />
              </div>
            ))}
          </div>

          {!hideFiltersButton && (
            <HideOrShowFiltersButton
              buttonText={isOpen ? "Skjul valg" : "Vis valg"}
              onClick={(e) => {
                e.preventDefault();
                setIsOpen(!isOpen);
              }}
            />
          )}
        </div>

        {/* Vis info om hva bruker har valgt så langt */}
        {isOpen && (
          <div className="step-indicator__information">
            {selectedValues && selectedValues.length > 0 ? (
              <>
                <Text size="small" value="Du har valgt" weight="bold" />
                <HorizontalSpacer />

                {steps.map((step, index) => {
                  const stepValue = selectedValues.find((sv) => sv.name === step.title)?.value ?? "";
                  const stepTitle = step.shortTitle ?? step.title;

                  return (
                    <Fragment key={step.id}>
                      {selectedValues.some((sv) => sv.name === step.title) ? (
                        <HorizontalContainer horizontalPlacement="space" noWrap={true}>
                          <HorizontalContainer verticalPlacement="center">
                            <StepIndicatorCircle
                              currentStepIndex={currentStepIndex}
                              goTo={goTo}
                              selectedValues={selectedValues}
                              stepIndex={selectedValues.find((sv) => sv.name === step.title)?.stepIndex ?? 0}
                            />
                            <VerticalSpacer size="x-small" />

                            <Text
                              size="small"
                              value={`${stepTitle}${stepValue ? `: ${stepValue}` : ""}`}
                              weight={index === currentStepIndex ? "bold" : undefined}
                            />
                          </HorizontalContainer>

                          {goTo && !step.notEditableInStepIndicator && (
                            <button type="button" onClick={() => goTo(index)}>
                              <Text size="small" value="Endre" />
                            </button>
                          )}
                        </HorizontalContainer>
                      ) : (
                        <HorizontalContainer verticalPlacement="center">
                          <StepIndicatorCircle currentStepIndex={currentStepIndex} stepIndex={index} />
                          <VerticalSpacer size="x-small" />

                          <Text
                            noWrap={true}
                            size="small"
                            value={stepTitle}
                            weight={index === currentStepIndex ? "bold" : undefined}
                          />
                        </HorizontalContainer>
                      )}

                      <HorizontalSpacer />
                    </Fragment>
                  );
                })}
              </>
            ) : (
              <Text size="small" style="italic" value="Du har ikke valgt noe ennå" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface StepIndicatorCircleProps extends StepIndicatorAllProps {
  stepIndex: number;
}

const StepIndicatorCircle = ({ currentStepIndex, goTo, selectedValues, stepIndex }: StepIndicatorCircleProps) => {
  return goTo && selectedValues?.find((sv) => stepIndex <= sv.stepIndex) ? (
    <button type="button" onClick={() => goTo(stepIndex)}>
      <div
        className={classNames(
          "step-indicator__circle",
          stepIndex <= currentStepIndex && "step-indicator__circle--active",
        )}
      >
        {stepIndex + 1}
      </div>
    </button>
  ) : (
    <div
      className={classNames(
        "step-indicator__circle",
        stepIndex <= currentStepIndex && "step-indicator__circle--active",
      )}
    >
      {stepIndex + 1}
    </div>
  );
};

interface StepIndicatorSkeletonProps {
  numberOfSteps: number;
}

export const StepIndicatorSkeleton = ({ numberOfSteps }: StepIndicatorSkeletonProps) => {
  const totalWidth = numberOfSteps - 1;

  return (
    <div className="step-indicator-wrapper">
      <div className="step-indicator">
        <div className="step-indicator__steps">
          {Array.from({ length: numberOfSteps }, (_, i) => (
            <div key={i} style={i < numberOfSteps - 1 ? { width: `calc((100%) / ${totalWidth})` } : {}}>
              <TextSkeleton size="small" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
